<template>
  <app-page wide :title="$t('@campaigns/title')">
    <v-layout>
      <v-flex xs3 class="pa-2">
        <v-select
          :label="$t('@campaigns/activeLabel')"
          v-model="activeFilter"
          :items="[
            { text: $t('@campaignsList/activeFilter/null'), value: null },
            { text: $t('@campaignsList/activeFilter/true'), value: true },
            { text: $t('@campaignsList/activeFilter/false'), value: false },
          ]"
        ></v-select>
      </v-flex>
      <v-flex xs3 class="pa-2">
        <v-select
          :label="$t('@campaigns/typeLabel')"
          v-model="typeFilter"
          :items="[
            { text: $t('@campaignsList/type/null'), value: null },
            { text: $t('@campaignsList/type/winback'), value: 'winback' },
            { text: $t('@campaignsList/type/signup'), value: 'signup' },
          ]"
        ></v-select>
      </v-flex>
      <v-spacer></v-spacer>
    </v-layout>
    <v-layout>
      <v-flex xs12>
        <errors-list :errorMessage="errorMessage" />

        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :rows-per-page-items="rowsPerPageItems"
          :total-items="pagination.totalItems"
          :pagination.sync="pagination"
        >
          <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
          <template slot="items" slot-scope="props">
            <tr>
              <grid-cell
                :header="header"
                :row="props.item"
                :key="index"
                v-for="(header, index) in headers"
                :style="applyStartedEndedStyle(header, props.item)"
                @action="onAction"
                :tooltip="header.value === 'productIds' ? mapProducts(props.item.productIds) : null"
              ></grid-cell>
            </tr>
          </template>
        </v-data-table>
      </v-flex>
    </v-layout>

    <v-btn
      v-if="isAuthAdmin"
      fab
      dark
      color="pink"
      top
      right
      absolute
      style="top: 32px; right: -80px"
      @click="createCampaign()"
    >
      <v-icon>add</v-icon>
    </v-btn>

    <v-dialog v-model="modal" persistent lazy full-width width="480px">
      <campaign-form
        :key="(selectedCampaign && selectedCampaign.id) || Date.now()"
        :campaignId="selectedCampaign && selectedCampaign.id"
        @close-modal="closeModal"
      />
    </v-dialog>
  </app-page>
</template>

<script>
import { mapGetters } from 'vuex';
import ErrorsList from '../../components/ErrorsList';
import GridCell from '../../components/GridCell';
import { getErrorMessage } from '../../errorHandlers';
import { defaultDateFormatter, defaultDateTimeFormatter, serializeQuery } from '../../utils';
import CampaignForm from './CampaignForm';

export default {
  components: { GridCell, CampaignForm, ErrorsList },

  data() {
    return {
      activeFilter: null,
      typeFilter: null,
      params: {},
      date: null,
      items: [],
      selectedCampaign: null,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 15,
        sortBy: 'availableFrom',
        totalItems: 0,
      },
      rowsPerPageItems: [10, 15, 50, 100],
      headers: [
        // { text: this.$t('@campaignsList/id'), value: 'id', sortable: false },
        {
          text: this.$t('@campaignsList/type'),
          value: 'type',
          sortable: false,
          format: (value) => this.$t(`@campaignsList/type/${value}`),
        },
        { text: this.$t('@campaignsList/active'), value: 'active', sortable: false },
        { text: this.$t('@campaignsList/promoted'), value: 'promoted', sortable: false },
        { text: this.$t('@campaignsList/name'), value: 'name', sortable: false },
        {
          text: this.$t('@campaignsList/products'),
          value: 'productIds',
          sortable: false,
          format: (value) => `${value.length}`,
        },
        // { text: this.$t('@campaignsList/description'), value: 'description', sortable: false },
        {
          text: this.$t('@campaignsList/discountPercent'),
          value: 'discountPercent',
          sortable: false,
        },
        {
          text: this.$t('@campaignsList/appliesForPeriods'),
          value: 'appliesForPeriods',
          sortable: false,
        },
        {
          text: this.$t('@campaignsList/availableFrom'),
          value: 'availableFrom',
          sortable: true,
          format: defaultDateFormatter,
        },
        {
          text: this.$t('@campaignsList/availableUntil'),
          value: 'availableUntil',
          format: defaultDateFormatter,
          sortable: false,
        },
        // {
        //   text: this.$t('@campaignsList/created'),
        //   value: 'created',
        //   sortable: true,
        //   format: defaultDateTimeFormatter,
        // },
        {
          text: this.$t('@campaignsList/updated'),
          value: 'updated',
          sortable: false,
          format: defaultDateTimeFormatter,
        },
      ],
      modal: false,
      loading: false,
      errorMessage: '',
      products: [],
    };
  },

  computed: {
    ...mapGetters(['isAuthAdmin']),
  },

  watch: {
    pagination() {
      this.fetchData();
    },
    activeFilter() {
      this.fetchData();
    },
    typeFilter() {
      this.fetchData();
    },
  },

  methods: {
    mapProducts(ids) {
      if (!ids || !this.products.length) {
        return;
      }

      return ids
        .map((id) => {
          const product = this.products.find((p) => p.id === id);
          return product ? product.name : id;
        })
        .join(', ');
    },
    applyStartedEndedStyle(header, item) {
      if (
        (header.value == 'availableFrom' && item.hasStarted) ||
        (header.value == 'availableUntil' && item.hasEnded)
      ) {
        return { color: 'lightgray' };
      }

      return {};
    },
    onAction({ action, row }) {
      if (!action) {
        this.selectedCampaign = row;
        this.modal = true;
      }
    },
    createCampaign() {
      this.selectedCampaign = null;
      this.modal = true;
    },
    async closeModal(isSent = false) {
      this.modal = false;
      if (isSent) {
        await this.fetchData();
      }
      this.selectedCampaign = null;
    },

    async updateRoute(query) {
      await this.$router
        .replace({
          name: 'campaignList',
          query: { ...this.$route.query, ...query },
        })
        .catch(() => {});
    },

    async fetchProducts() {
      if (this.products.length) {
        return;
      }

      try {
        const { data } = await this.$http.get('/api/products');
        this.products = data;
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      }
    },

    async fetchData() {
      try {
        this.loading = true;
        const skip = (this.pagination.page - 1) * this.pagination.rowsPerPage;
        const take = this.pagination.rowsPerPage;
        const query = {
          ...this.params,
          skip: skip,
          take: take,
        };

        if (this.activeFilter !== null) {
          query.active = this.activeFilter;
        } else {
          delete query.active;
        }

        if (this.typeFilter !== null) {
          query.type = this.typeFilter;
        } else {
          delete query.type;
        }

        if (this.pagination.sortBy) {
          query.sortBy = this.pagination.sortBy;
          query.sortOrder = this.pagination.descending ? 'DESC' : 'ASC';
        } else {
          delete query.sortBy;
          delete query.sortOrder;
        }

        await this.updateRoute(query);

        const queryString = serializeQuery(query);
        const {
          data: {
            items,
            meta: { total },
          },
        } = await this.$http.get(`/api/campaigns?${queryString}`);
        this.items = items;
        this.pagination.totalItems = total;
        this.errorMessage = '';
      } catch (e) {
        this.errorMessage = getErrorMessage(e);
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.params = { ...this.params, ...this.$router.currentRoute.query };
    this.fetchData();
    this.fetchProducts();
  },

  beforeRouteUpdate(to, from, next) {
    this.params = { ...this.params, ...to.query };
    next();
  },
};
</script>
